'use client';

import DownloadIcon from '@mui/icons-material/Download';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import {
  Avatar,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
  type MRT_SortingState,
} from 'material-react-table';
import { Link as RouterLink } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchInventoryAuditorCountSnapshotDates,
  fetchInventoryAuditorCountSnapshotQuery,
  type InventoryAuditorCountSnapshotRow,
} from '@/hooks/useInventoryAuditorInventory';
import { useNotification } from '@/contexts/NotificationContext';

function formatIaCountDateLabel(ymd: string): string {
  const parts = ymd.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return ymd;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function csvEscape(cell: string): string {
  if (/[,"\n\r]/.test(cell)) {
    return `"${cell.replace(/"/g, '""')}"`;
  }
  return cell;
}

function slugLocationBasename(name: string): string {
  const slug = name
    .trim()
    .replace(/[^a-z0-9-_]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
    .slice(0, 48);
  return slug || 'location';
}

/** Prefer UPC-A (12) when stored as zero-padded EAN-13. */
function displayUpc(raw: string | null | undefined): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '—';
  if (digits.length === 13 && digits.startsWith('0')) return digits.slice(1);
  return digits;
}

const SORTABLE = new Set(['upc', 'name', 'quantity']);

const InventoryAuditorLocationCountHistoryPanel: React.FC<{
  locationId: number;
  locationName: string;
  initialCountDate?: string;
}> = ({ locationId, locationName, initialCountDate }) => {
  const [dates, setDates] = useState<string[]>([]);
  const [datesLoading, setDatesLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<InventoryAuditorCountSnapshotRow[]>([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'upc', desc: false }]);
  const [exporting, setExporting] = useState(false);

  const { showError, showSuccess } = useNotification();

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [debouncedSearch, selectedDate]);

  useEffect(() => {
    let cancelled = false;
    setDatesLoading(true);
    void (async () => {
      try {
        const { dates: ds } = await fetchInventoryAuditorCountSnapshotDates(locationId);
        if (cancelled) return;
        setDates(ds);
        const preferred =
          initialCountDate && ds.includes(initialCountDate) ? initialCountDate : undefined;
        setSelectedDate((prev) => {
          if (preferred) return preferred;
          return prev !== '' && ds.includes(prev) ? prev : ds[0] ?? '';
        });
      } catch (e: unknown) {
        if (!cancelled) {
          showError(e instanceof Error ? e.message : 'Failed to load count dates');
          setDates([]);
          setSelectedDate('');
        }
      } finally {
        if (!cancelled) setDatesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialCountDate, locationId, showError]);

  const orderBy = sorting[0]?.id && SORTABLE.has(sorting[0].id) ? sorting[0].id : 'upc';
  const orderDirection = sorting[0]?.desc ? 'DESC' : 'ASC';

  const reload = useCallback(async () => {
    if (!selectedDate) {
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const r = await fetchInventoryAuditorCountSnapshotQuery({
        locationId,
        countDate: selectedDate,
        skip: pagination.pageIndex * pagination.pageSize,
        take: pagination.pageSize,
        search: debouncedSearch,
        orderBy,
        orderDirection,
      });
      setRows(r.data);
      setTotal(r.total);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load snapshot');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearch,
    locationId,
    orderBy,
    orderDirection,
    pagination.pageIndex,
    pagination.pageSize,
    selectedDate,
    showError,
  ]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleExportCsv = useCallback(async () => {
    if (!selectedDate) return;
    setExporting(true);
    try {
      const chunks: InventoryAuditorCountSnapshotRow[] = [];
      let skip = 0;
      const take = 3000;
      let capRows = 10_000;
      let firstResp: {
        location: { id: number; name?: string };
        total: number;
        countDate: string;
      } | null = null;
      while (chunks.length < capRows) {
        const r = await fetchInventoryAuditorCountSnapshotQuery({
          locationId,
          countDate: selectedDate,
          skip,
          take,
          search: '',
          orderBy: 'upc',
          orderDirection: 'ASC',
        });
        if (!firstResp) {
          firstResp = { location: r.location, total: r.total, countDate: r.countDate };
        }
        capRows = Math.min(r.total, 10_000);
        chunks.push(...r.data);
        if (r.data.length === 0 || chunks.length >= capRows || skip + r.data.length >= r.total) break;
        skip += r.data.length;
      }
      if (chunks.length === 0 || !firstResp) {
        showError('Nothing to export for this count day.');
        return;
      }
      const locNameCsv = csvEscape(firstResp.location?.name ?? locationName ?? '');
      const locIdCsv = String(firstResp.location?.id ?? locationId);
      const countDateCsv = firstResp.countDate ?? selectedDate;
      const header = [
        'count_date',
        'location_id',
        'location_name',
        'upc',
        'item_name',
        'uom_pack_type',
        'size',
        'quantity',
        'last_updated',
      ];
      const bodyRows = chunks.map((row) => {
        const last = row.updatedAt ? new Date(row.updatedAt).toISOString() : '';
        return [
          csvEscape(row.countDate ?? countDateCsv),
          locIdCsv,
          locNameCsv,
          csvEscape(row.upc ?? ''),
          csvEscape(row.name?.trim() || ''),
          csvEscape(row.uomPackType ?? ''),
          csvEscape(row.size ?? ''),
          String(row.quantity),
          csvEscape(last),
        ].join(',');
      });
      const csvText = [header.join(','), ...bodyRows].join('\n');
      const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `inventory-auditor-count-${slugLocationBasename(firstResp.location?.name ?? locationName)}-${countDateCsv}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      if (firstResp.total > 10_000) {
        showSuccess(`CSV downloaded (${chunks.length.toLocaleString()} rows; capped at 10,000).`);
      } else {
        showSuccess('CSV downloaded');
      }
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [locationId, locationName, selectedDate, showError, showSuccess]);

  const handleChangeDate = (e: SelectChangeEvent<string>) => {
    setSelectedDate(e.target.value);
  };

  const columns = useMemo<MRT_ColumnDef<InventoryAuditorCountSnapshotRow>[]>(
    () => [
      {
        id: 'image',
        header: '',
        size: 52,
        enableSorting: false,
        Cell: ({ row }) => (
          <Avatar variant="rounded" src={row.original.imageUrl || undefined} sx={{ width: 36, height: 36 }}>
            <Inventory2Icon fontSize="small" />
          </Avatar>
        ),
      },
      {
        accessorKey: 'upc',
        header: 'UPC',
        size: 140,
        Cell: ({ cell }) => displayUpc(cell.getValue<string>()),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 240,
        Cell: ({ cell }) => cell.getValue<string | null>()?.trim() || '—',
      },
      {
        accessorKey: 'uomPackType',
        header: 'UOM',
        size: 90,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? '—',
      },
      {
        accessorKey: 'size',
        header: 'Size',
        size: 90,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? '—',
      },
      {
        accessorKey: 'quantity',
        header: 'Qty',
        size: 80,
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        size: 140,
        enableSorting: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return v
            ? new Date(v).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
            : '—';
        },
      },
    ],
    [],
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Shows quantities recorded when counts were{' '}
        <Typography component="span" sx={{ fontWeight: 600 }} variant="body2">
          saved
        </Typography>{' '}
        for this calendar day. Items not scanned that day do not appear. Re-saving the same item later the same day
        replaces the prior quantity.
      </Typography>

      {datesLoading ? (
        <Typography variant="body2" color="text.secondary">
          Loading count dates…
        </Typography>
      ) : dates.length === 0 ? (
        <Typography variant="body2" sx={{ mb: 2 }}>
          No saved count history for this location yet.
          <Button variant="text" component={RouterLink} to={`/app/inventory-auditor/locations/${locationId}/site-visit`}>
            Full store count
          </Button>
        </Typography>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, maxWidth: 640 }}>
            The bold YYYY-MM-DD is the server count calendar (optional env IA_COUNT_DATE_TZ). The second line is that
            same calendar day formatted in your browser locale.
          </Typography>
          <MaterialReactTable
            columns={columns}
            data={rows}
            rowCount={total}
            manualPagination
            manualSorting
            enableGlobalFilter={false}
            enableColumnFilters={false}
            enableDensityToggle
            enableFullScreenToggle
            enableHiding
            enableColumnResizing
            enableStickyHeader
            onPaginationChange={setPagination}
            onSortingChange={setSorting}
            initialState={{ density: 'compact' }}
            state={{ isLoading: loading, pagination, sorting }}
            muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 360px)' } }}
            renderEmptyRowsFallback={() => (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No matching lines for this count day or search.{' '}
                <Button
                  variant="text"
                  component={RouterLink}
                  to={`/app/inventory-auditor/locations/${locationId}/site-visit`}
                >
                  Full store count
                </Button>
              </Typography>
            )}
            renderTopToolbarCustomActions={() => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', py: 0.5 }}>
                <FormControl sx={{ minWidth: 220 }} size="small">
                  <InputLabel id="ia-count-history-date-label">Count day</InputLabel>
                  <Select<string>
                    labelId="ia-count-history-date-label"
                    label="Count day"
                    value={selectedDate}
                    onChange={handleChangeDate}
                  >
                    {dates.map((ymd) => (
                      <MenuItem key={ymd} value={ymd}>
                        <Box>
                          <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                            {ymd}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {formatIaCountDateLabel(ymd)}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  placeholder="Search UPC / name"
                  value={search}
                  onChange={(ev) => setSearch(ev.target.value)}
                  sx={{ minWidth: 220 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={exporting || !selectedDate || total === 0}
                  startIcon={<DownloadIcon />}
                  onClick={() => void handleExportCsv()}
                  sx={{ textTransform: 'none' }}
                >
                  {exporting ? 'Exporting…' : 'Export CSV'}
                </Button>
              </Box>
            )}
          />
        </>
      )}
    </Box>
  );
};

export default InventoryAuditorLocationCountHistoryPanel;
